import React from 'react';
import { Share, PlusSquare, Check, Copy, Download } from 'lucide-react';
import AppLogo from './AppLogo';
import { platform, canPromptInstall, promptInstall, onInstallAvailabilityChange } from '../lib/pwa';
import { centerToast } from './CenterToast';

/**
 * InstallScreen — the one-screen invitation to add IhsanOne to the home screen.
 *
 * Three variants, because the three platforms genuinely differ:
 *   Chromium  — we hold a real beforeinstallprompt, so one button does it
 *   iOS       — Safari has no API at all; the member must be walked through it
 *   in-app    — WhatsApp and friends cannot install anything, so send them out
 *
 * One primary action, one "Not now", and dismissal is respected (see
 * lib/installPrompt.js). No second chances dressed up as a different question.
 */

const Step = ({ n, icon: Icon, children }) => (
  <li className="flex items-start gap-3">
    <span className="flex-shrink-0 h-7 w-7 rounded-full bg-[#0F4C4A] text-white text-[13px] font-bold flex items-center justify-center">
      {n}
    </span>
    <span className="flex-1 text-sm text-foreground leading-relaxed pt-0.5">
      {children}
    </span>
    {Icon && <Icon className="h-5 w-5 text-[#0F4C4A] flex-shrink-0 mt-1" />}
  </li>
);

export default function InstallScreen({ onDismiss, onInstalled }) {
  const kind = platform();
  const [promptable, setPromptable] = React.useState(canPromptInstall());

  React.useEffect(() => onInstallAvailabilityChange(() => setPromptable(canPromptInstall())), []);

  const handleInstall = async () => {
    const outcome = await promptInstall();
    if (outcome === 'accepted') {
      onInstalled?.();
    } else if (outcome === 'unavailable') {
      centerToast('Use your browser menu to add IhsanOne', 'ℹ️');
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      centerToast('Link copied', '🔗');
    } catch {
      centerToast('Could not copy the link', '⚠️');
    }
  };

  const showChromiumButton = promptable && (kind === 'android-chromium' || kind === 'desktop-chromium');

  return (
    <div
      className="fixed inset-0 z-[80] bg-background flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label="Install IhsanOne"
      data-testid="install-screen"
    >
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-[480px] px-6 pt-12 pb-6 flex flex-col items-center text-center">
          <AppLogo size="lg" />
          <h1 className="mt-4 text-xl font-bold text-foreground">Add IhsanOne to your phone</h1>
          <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed max-w-[300px]">
            It opens like an app, straight from your home screen — no browser, no typing the
            address each time.
          </p>

          <div className="w-full mt-7 text-left">
            {kind === 'ios-safari' && (
              <>
                <ol className="space-y-4" data-testid="install-steps-ios">
                  <Step n={1} icon={Share}>
                    Tap the <span className="font-semibold">Share</span> button at the bottom of
                    Safari.
                  </Step>
                  <Step n={2} icon={PlusSquare}>
                    Scroll down and choose{' '}
                    <span className="font-semibold">Add to Home Screen</span>.
                  </Step>
                  <Step n={3} icon={Check}>
                    Tap <span className="font-semibold">Add</span>, top right.
                  </Step>
                </ol>
                <p className="mt-5 rounded-xl bg-muted/50 border border-border p-3 text-xs text-muted-foreground leading-relaxed">
                  You&apos;ll sign in once more inside the app — use the 6-digit code from your
                  email. The installed app keeps its own sign-in, separate from Safari.
                </p>
              </>
            )}

            {kind === 'in-app-browser' && (
              <div data-testid="install-steps-inapp" className="space-y-4">
                <p className="text-sm text-foreground leading-relaxed">
                  You opened this from inside another app, which can&apos;t install IhsanOne. Open
                  it in Safari or Chrome first.
                </p>
                <button
                  type="button"
                  onClick={copyLink}
                  data-testid="install-copy-link"
                  className="w-full h-12 rounded-full border border-border bg-white text-sm font-semibold text-foreground flex items-center justify-center gap-2"
                >
                  <Copy className="h-4 w-4" />
                  Copy the link
                </button>
              </div>
            )}

            {(kind === 'android-chromium' || kind === 'desktop-chromium' || kind === 'other') && (
              <ol className="space-y-4" data-testid="install-steps-chromium">
                {showChromiumButton ? (
                  <Step n={1} icon={Download}>
                    Tap <span className="font-semibold">Install</span> below and confirm.
                  </Step>
                ) : (
                  <>
                    <Step n={1}>
                      Open your browser&apos;s menu (the three dots).
                    </Step>
                    <Step n={2} icon={PlusSquare}>
                      Choose <span className="font-semibold">Install app</span> or{' '}
                      <span className="font-semibold">Add to Home screen</span>.
                    </Step>
                  </>
                )}
              </ol>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-[480px] px-6 pb-8 pt-2 space-y-2">
        {showChromiumButton && (
          <button
            type="button"
            onClick={handleInstall}
            data-testid="install-primary"
            className="w-full h-12 rounded-full bg-[#0F4C4A] text-white text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.99] transition-transform"
          >
            <Download className="h-4 w-4" />
            Install
          </button>
        )}
        <button
          type="button"
          onClick={onDismiss}
          data-testid="install-dismiss"
          className="w-full h-12 rounded-full text-sm font-semibold text-muted-foreground"
        >
          Not now
        </button>
      </div>
    </div>
  );
}
